import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { createGlobalStyle, ThemeProvider as StyledThemeProvider } from "styled-components";
import { ToastContainer } from 'react-toastify';
import Home from './components/Home';
import Cases from './components/Cases';
import ProfileSection from './components/ProfileSection';
import CaseView from './components/CaseView';
import CaseResult from './components/CaseResult';
import SkinDetails from './components/SkinDetails';
import Packages from './components/Packages';
import TradeUrlPage from './components/TradeUrlPage';
import TokenFarming from './components/TokenFarming';
import Referrals from './components/Referrals';
import MySkins from './components/MySkins';
import SupportPage from './components/SupportPage';
import TicketChat from './components/TicketChat';
import { ThemeProvider, useThemeContext } from './contexts/ThemeContext';
import { SoundProvider } from './contexts/SoundContext';
import { useAppLanguage } from './hooks/useAppLanguage';
import { CollectionProvider } from './contexts/CollectionContext';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import WebApp from '@twa-dev/sdk';
import WithdrawPage from './components/WithdrawPage';
import useUserStore from './stores/userStore';
import usePackageStore from './stores/packageStore';
import useCasesStore from './stores/casesStore';
import useReferralStore from './stores/referralStore';
import useInventoryStore from './stores/inventoryStore';
import { useTranslation } from 'react-i18next';
import ScrollToTop from './components/ScrollToTop';
import LoginView from './components/LoginView';
import OnboardingView from './components/OnboardingView';
import api from './api/axios';
import React from 'react';
import { AgreementContext } from './contexts/AgreementContext';
import { useMaintenanceCheck } from './hooks/useMaintenanceCheck';

const GlobalStyles = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text.primary};
    overflow: hidden;
  }

  * {
    box-sizing: border-box;
  }

  /* Стили для скроллбара */
  ::-webkit-scrollbar {
    width: 0px;
  }

  /* Стили для toast-уведомлений */
  .Toastify__toast {
    background: ${({ theme }) => theme.card.background};
    color: ${({ theme }) => theme.text.primary};
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .Toastify__toast-body {
    font-size: 14px;
    font-weight: 500;
  }

  .Toastify__progress-bar {
    background: ${({ theme }) => theme.primary};
  }
`;

const AppContent = ({ setAgreed }) => {
  const { currentTheme } = useThemeContext();
  const auth = useUserStore(state => state.auth);
  const fetchUser = useUserStore(state => state.fetchUser);
  const fetchPackages = usePackageStore(state => state.fetchPackages);
  const fetchCases = useCasesStore(state => state.fetchCases);
  const fetchReferrals = useReferralStore(state => state.fetchReferrals);
  const fetchItems = useInventoryStore(state => state.fetchItems);
  useAppLanguage();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agreed } = React.useContext(AgreementContext);

  const [agreementsChecked, setAgreementsChecked] = React.useState(false);
  const [pendingStartParam, setPendingStartParam] = React.useState(null);

  // Авторизация и загрузка пользователя и данных
  React.useEffect(() => {
    const doAuthAndLoad = async () => {
      const result = await auth();
      if (result.success) {
        await fetchUser();
        await fetchPackages();
        await fetchCases();
        await fetchReferrals();
        await fetchItems();
      }
    };
    doAuthAndLoad();
  }, [auth, fetchUser, fetchPackages, fetchCases, fetchReferrals, fetchItems]);

  // Функция для обработки start_param
  const handleStartParam = (startParam) => {
    if (!startParam) return;
    console.log('startParam:', startParam);
    const match = startParam.match(/^item_([a-f0-9-]+)_ref([a-f0-9]+)$/);
    if (match) {
      const [, itemId] = match;
      console.log('Parsed itemId:', itemId);
      if (!agreed) {
        setPendingStartParam({ itemId });
        return;
      }
      navigate(`/skin/${itemId}`);
    } else {
      console.log('startParam format not recognized:', startParam);
    }
  };

  React.useEffect(() => {
    const checkAgreements = async () => {
      try {
        const res = await api.get('/user/check-agreements');
        setAgreed(res.success && res.agreed);
      } catch (e) {
        setAgreed(false);
      } finally {
        setAgreementsChecked(true);
      }
    };
    checkAgreements();
  }, []);

  // Обработка start_param при загрузке
  React.useEffect(() => {
    if (!agreementsChecked) return;
    const startParam = WebApp.initDataUnsafe.start_param;
    handleStartParam(startParam);
  }, [agreementsChecked]);

  // Обработка редиректа после согласия с условиями
  React.useEffect(() => {
    if (agreed && pendingStartParam) {
      console.log('User agreed, redirecting to:', pendingStartParam.itemId);
      navigate(`/skin/${pendingStartParam.itemId}`);
      setPendingStartParam(null);
    }
  }, [agreed, pendingStartParam, navigate]);

  // Обработка редиректа на онбординг, если не согласился
  React.useEffect(() => {
    if (agreementsChecked && !agreed) {
      navigate('/onboarding', { replace: true });
    }
  }, [agreementsChecked, agreed, navigate]);

  React.useEffect(() => {
    console.log('WebApp.platform', WebApp.platform);
    if (WebApp.platform === 'android' || WebApp.platform === 'ios') {
      WebApp.requestFullscreen();
      WebApp.expand();
    }
  }, []);

  if (!agreementsChecked) {
    return <div style={{ color: '#fff', textAlign: 'center', marginTop: 64 }}>{t('common.loading') || 'Загрузка...'}</div>;
  }

  return (
    <StyledThemeProvider theme={currentTheme}>
      <GlobalStyles />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="cases" element={<Cases />} />
          <Route path="skins" element={<MySkins />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="profile" element={<ProfileSection />} />
          <Route path="case-result/:assetId" element={<CaseResult />} />
          <Route path="skin/:weaponId" element={<SkinDetails />} />
          <Route path="packages" element={<Packages />} />
          <Route path="trade-url" element={<TradeUrlPage />} />
          <Route path="farm" element={<TokenFarming />} />
          <Route path="withdraw" element={<WithdrawPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="support/ticket/:ticketId" element={<TicketChat />} />
        </Route>
        <Route path="/login" element={<LoginView />} />
        <Route path="/onboarding" element={agreed ? <Navigate to="/" replace /> : <OnboardingView setAgreed={setAgreed} />} />
        <Route path="/case/:caseId" element={<CaseView />} />
        <Route path="/case-result/:weaponId" element={<CaseResult />} />
        <Route path="/inventory" element={<MySkins />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer position="bottom-center" autoClose={3000} />
    </StyledThemeProvider>
  );
};

function MaintenancePage() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
      <h1 style={{ fontSize: 32, marginBottom: 16 }}>Технические работы</h1>
      <p style={{ fontSize: 18, color: '#888' }}>Сервис временно недоступен.<br />Пожалуйста, попробуйте позже.</p>
    </div>
  );
}

function WebOnlyBlock() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
      <h1 style={{ fontSize: 28, marginBottom: 16, color: '#222' }}>Пожалуйста, откройте приложение в Telegram</h1>
      <p style={{ fontSize: 18, color: '#888', textAlign: 'center' }}>
        Данное приложение работает только внутри Telegram (мобильное приложение или десктоп-клиент).<br />
        Запустите его через Telegram, чтобы продолжить.
      </p>
    </div>
  );
}

function App() {
  const [agreed, setAgreed] = React.useState(false);
  const isMaintenance = useMaintenanceCheck();
  // Проверяем платформу до любых хуков с побочными эффектами
  if (WebApp.platform === 'web' || WebApp.platform === 'unknown') {
    return <WebOnlyBlock />;
  }


  if (isMaintenance) return <MaintenancePage />;
  return (
    <ThemeProvider>
      <CollectionProvider>
        <SoundProvider>
          <AgreementContext.Provider value={{ agreed }}>
            <AppContent setAgreed={setAgreed} />
          </AgreementContext.Provider>
        </SoundProvider>
      </CollectionProvider>
    </ThemeProvider>
  );
}

export default App;